import React from 'react';
import { Select, MenuItem, FormControl, SelectChangeEvent, Typography, InputAdornment, IconButton, OutlinedInput } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PracticeProvider } from 'pages/Dashboard/types/providers.types';
import Theme from 'theme';
import { useHttp } from 'hooks/use-fetch';

interface PatientDropdownProps {
  providers: PracticeProvider[]; // You can pass the list of patients as a prop
  selectedProvider: string;
  setSelectedProvider: (provider:string) => void;
}

function SelectProvider({
  providers,
  selectedProvider,
  setSelectedProvider,
}: PatientDropdownProps) {
  const { setSelectedProviderName, setSelectedProviderId } = useHttp();
  const handleChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    if (value === '') {
      setSelectedProviderId('');
      setSelectedProvider('');
      setSelectedProviderName('');
      return;
    }
    setSelectedProviderId(value);
    setSelectedProvider(value);
    const selectedProviderObj = providers.find((provider) => provider.id === value);
    if (selectedProviderObj) {
      setSelectedProviderName(`${selectedProviderObj.firstName} ${selectedProviderObj.lastName}`);
    }
  };

  return (
    <FormControl variant='outlined' size='small' style={styles.selectControl}>
      <Select
        displayEmpty
        labelId='provider-select-label'
        value={selectedProvider}
        onChange={handleChange}
        style={styles.selectControl}
        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
        input={
          (
            <OutlinedInput
              endAdornment={
                selectedProvider ? (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='clear selection'
                      onClick={() => {
                        setSelectedProvider('');
                        setSelectedProviderId('');
                        setSelectedProviderName('');
                      }}
                      edge='end'
                      size='small'
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
              sx={{ height: '44px', ...(selectedProvider ? { '& .MuiSelect-icon': { display: 'none' } } : {}) }}
            />
          )
        }
        IconComponent={selectedProvider ? () => null : undefined}
        renderValue={(selected) => {
          if (!selected) {
            return <Typography variant='body1' color='text.secondary'>Filter by clinician</Typography>;
          }
          const provider = providers.find((p) => p.id === selected);
          return provider ? `${provider.firstName} ${provider.lastName}` : '';
        }}
      >
        {providers.map((provider) => (
          <MenuItem key={provider.id} value={provider.id}>
            <Typography variant='body1' color={Theme.custom.colors.lightTextSecondary} fontWeight={400}>
              {`${provider.firstName} ${provider.lastName}`}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const styles = {
  label: {
    fontSize: '16px',
    color: Theme.custom.colors.lightTextSecondary,
  },
  selectControl: {
    minWidth: '320px',
    width: '100%',
    backgroundColor: '#fff',
    height: '44px',
  },
};

export default SelectProvider;
